import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { makeid, stringToBool, surveyDisplayNumbers } from "../../utility";
import ConfirmDeleteDialog from "../ConfirmDeleteDialog";
import FloatingAddContentButton from "../FloatingAddContentButton";
import NoPermissionsMessage from "../NoPermissionsMessage";
import ProjectSelectFileDialog from "../projects/ProjectSelectFileDialog";
import ToolbarWithLogo from "../ToolbarWithLogo";
import ShareSurveyDialog from "./ShareSurveyDialog";
import SurveyQuestionEdit from "./SurveyQuestionEdit";

const surfaceSx = {
    borderRadius: 3,
    borderColor: "divider",
    backgroundColor: "background.paper",
    boxShadow: "0 8px 28px rgba(20, 35, 25, 0.065)",
};

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

function surveyFromSnapshot(snapshot) {
    const data = snapshot.val();
    const questions = Object.entries(data?.questions || {}).map(
        ([questionKey, question]) => ({
            key: questionKey,
            row: question.row,
            questionText: question.questionText,
            answerFormat: question.answerFormat,
            answerRequired: stringToBool(question.answerRequired),
            fileType: question.fileType,
            linkKey: question.linkKey,
            multipleChoiceOptions: Object.entries(
                question.multipleChoiceOptions || {},
            ).map(([optionKey, option]) => ({
                key: optionKey,
                index: option.index,
                text: option.text,
            })),
        }),
    );

    return {
        key: snapshot.key,
        questions: questions.sort((a, b) => a.row - b.row),
    };
}

export default function SurveyEditPage() {
    const { currentUserData } = useContext(UserContext);
    const {
        surveyInfo = [],
        surveyInfoLoaded = false,
        groups = [],
        groupsLoaded = false,
    } = useContext(DatabaseContext);
    const location = useLocation();
    const history = useHistory();

    const [title, setTitle] = useState("");
    const [introduction, setIntroduction] = useState("");
    const [survey, setSurvey] = useState();
    const [statusType, setStatusType] = useState("error");
    const [statusMessage, setStatusMessage] = useState("");
    const [statusIsVisible, setStatusIsVisible] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [deleteConfirmDialogIsOpen, setDeleteConfirmDialogIsOpen] =
        useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [anonymousAnswers, setAnonymousAnswers] = useState(true);
    const [selectExistingFileDialogOpen, setSelectExistingFileDialogOpen] =
        useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const surveyKey = useMemo(
        () => new URLSearchParams(location.search).get("key"),
        [location.search],
    );

    const surveyInfoObject = useMemo(
        () => surveyInfo.find((item) => item.key === surveyKey),
        [surveyInfo, surveyKey],
    );

    const userCanEdit = useMemo(() => {
        if (!currentUserData || !surveyInfoLoaded || !groupsLoaded) {
            return undefined;
        }

        if (!surveyInfoObject) {
            return false;
        }

        if (surveyInfoObject.createdBy === currentUserData.key) {
            return true;
        }

        const matchingUser = (surveyInfoObject.users || []).find(
            (user) => user.key === currentUserData.key,
        );
        if (matchingUser?.canEdit) {
            return true;
        }

        return (surveyInfoObject.groups || []).some((surveyGroup) => {
            if (!surveyGroup.canEdit) {
                return false;
            }

            const matchingGroup = groups.find(
                (group) => group.key === surveyGroup.key,
            );
            return (matchingGroup?.users || []).some(
                (user) => user.key === currentUserData.key,
            );
        });
    }, [
        currentUserData,
        groups,
        groupsLoaded,
        surveyInfoLoaded,
        surveyInfoObject,
    ]);

    const canShareSurveys = Boolean(
        userCanEdit && currentUserData?.permissions?.createSurveys,
    );

    useEffect(() => {
        if (!surveyInfoObject) {
            return;
        }

        setTitle(surveyInfoObject.title || "");
        setIntroduction(surveyInfoObject.intro || "");
        setAnonymousAnswers(surveyInfoObject.anonymous ?? true);
    }, [surveyInfoObject]);

    useEffect(() => {
        if (!surveyKey || userCanEdit !== true) {
            setSurvey(undefined);
            return undefined;
        }

        const surveyRef = firebase.database().ref("surveys").child(surveyKey);
        const handleSnapshot = (snapshot) => {
            setSurvey(surveyFromSnapshot(snapshot));
        };

        surveyRef.on("value", handleSnapshot);
        return () => surveyRef.off("value", handleSnapshot);
    }, [surveyKey, userCanEdit]);

    function updateSurveyInfo(field, value) {
        if (!surveyKey) {
            return;
        }

        firebase
            .database()
            .ref("surveyinfo")
            .child(surveyKey)
            .child(field)
            .set(value);
    }

    function handleTitleChange(event) {
        const newValue = event.target.value;
        setTitle(newValue);
        updateSurveyInfo("title", newValue);
    }

    function handleIntroductionChange(event) {
        const newValue = event.target.value;
        setIntroduction(newValue);
        updateSurveyInfo("intro", newValue);
    }

    function handleAddComponentClick(event) {
        setAnchorEl(event.currentTarget);
    }

    function handleNewExistingFilePressed() {
        setSelectExistingFileDialogOpen(true);
        setAnchorEl(null);
    }

    function handleSelectExistingFileClose(selectedFile) {
        if (selectedFile && survey) {
            const newQuestion = {
                questionText: "",
                answerRequired: "false",
                answerFormat: "File",
                fileType: selectedFile.type,
                linkKey: selectedFile.key,
                row: survey.questions.length,
            };
            const questionRef = firebase
                .database()
                .ref(`surveys/${surveyKey}/questions`)
                .push();

            questionRef.set(newQuestion);
        }

        setSelectExistingFileDialogOpen(false);
    }

    function handleAddQuestionClick() {
        if (!survey) {
            return;
        }

        const optionKey = makeid(20);
        const newQuestion = {
            questionText: "",
            answerRequired: "true",
            answerFormat: "Short Text",
            row: survey.questions.length,
        };
        const questionRef = firebase
            .database()
            .ref(`surveys/${surveyKey}/questions`)
            .push();

        questionRef.set(newQuestion);
        questionRef
            .child("multipleChoiceOptions")
            .child(optionKey)
            .set({ index: 0, text: "Option 1", key: optionKey });
        setAnchorEl(null);
    }

    function handleDeleteQuestionClick(question) {
        if (!survey) {
            return;
        }

        survey.questions
            .filter((item) => item.key !== question.key)
            .sort((a, b) => a.row - b.row)
            .forEach((item, index) => {
                firebase
                    .database()
                    .ref("surveys")
                    .child(surveyKey)
                    .child("questions")
                    .child(item.key)
                    .update({ row: index });
            });

        firebase
            .database()
            .ref("surveys")
            .child(surveyKey)
            .child("questions")
            .child(question.key)
            .remove();
    }

    function moveQuestion(question, direction) {
        if (!survey) {
            return;
        }

        const currentIndex = survey.questions.findIndex(
            (item) => item.key === question.key,
        );
        const targetIndex = currentIndex + direction;

        if (
            currentIndex < 0 ||
            targetIndex < 0 ||
            targetIndex >= survey.questions.length
        ) {
            return;
        }

        const targetQuestion = survey.questions[targetIndex];
        firebase
            .database()
            .ref("surveys")
            .child(surveyKey)
            .child("questions")
            .child(targetQuestion.key)
            .update({ row: currentIndex });
        firebase
            .database()
            .ref("surveys")
            .child(surveyKey)
            .child("questions")
            .child(question.key)
            .update({ row: targetIndex });

        setSurvey((currentSurvey) => {
            const questions = [...currentSurvey.questions];
            questions[currentIndex] = {
                ...targetQuestion,
                row: currentIndex,
            };
            questions[targetIndex] = {
                ...question,
                row: targetIndex,
            };
            return { ...currentSurvey, questions };
        });
    }

    // The dialog reports the outcome itself, in one message that covers both who
    // it was shared with and what was delivered.
    function handleCloseShareDialog() {
        setShareDialogOpen(false);
    }

    async function handleCloseConfirmDeleteDialog(result) {
        if (!result || !surveyInfoObject) {
            setDeleteConfirmDialogIsOpen(false);
            return;
        }

        setDeleteInProgress(true);
        try {
            await Promise.all([
                firebase
                    .database()
                    .ref("surveyinfo")
                    .child(surveyInfoObject.key)
                    .remove(),
                firebase
                    .database()
                    .ref("surveys")
                    .child(surveyInfoObject.key)
                    .remove(),
            ]);
            history.replace("/surveys/s");
        } catch (error) {
            setDeleteInProgress(false);
            setDeleteConfirmDialogIsOpen(false);
            setStatusType("error");
            setStatusMessage(error.message || "Unable to delete this survey.");
            setStatusIsVisible(true);
        }
    }

    function handleAnonymousAnswersChange() {
        if (!surveyInfoObject) {
            return;
        }

        const newValue = !anonymousAnswers;
        firebase
            .database()
            .ref("surveyinfo")
            .child(surveyInfoObject.key)
            .update({ anonymous: newValue });
        setAnonymousAnswers(newValue);
    }

    const questionDisplayNumbers = surveyDisplayNumbers(survey?.questions);

    const isLoading =
        userCanEdit === undefined || (userCanEdit === true && !survey);

    return (
        <>
            {isLoading && (
                <Box
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "background.default",
                    }}
                >
                    <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                        <CircularProgress
                            size={32}
                            aria-label="Loading survey editor"
                        />
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Loading survey editor…
                        </Typography>
                    </Box>
                </Box>
            )}

            {userCanEdit === false && <NoPermissionsMessage />}

            {userCanEdit === true && survey && (
                <Box
                    component="main"
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        pb: 12,
                        backgroundColor: "background.default",
                    }}
                >
                    <Container
                        maxWidth="lg"
                        sx={{
                            px: { xs: 1.5, sm: 3 },
                            py: { xs: 2, sm: 3 },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                            }}
                        >
                            <Card variant="outlined" sx={surfaceSx}>
                                <CardContent
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        "&:last-child": {
                                            pb: { xs: 2, sm: 3 },
                                        },
                                    }}
                                >
                                    <IconButton
                                        aria-label="Back to surveys"
                                        onClick={() => history.goBack()}
                                        sx={{
                                            flexShrink: 0,
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <Box
                                        sx={{
                                            width: 46,
                                            height: 46,
                                            display: {
                                                xs: "none",
                                                sm: "grid",
                                            },
                                            placeItems: "center",
                                            flexShrink: 0,
                                            borderRadius: 2,
                                            color: "primary.main",
                                            backgroundColor:
                                                "rgba(0, 149, 61, 0.09)",
                                        }}
                                    >
                                        <AssignmentOutlinedIcon />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="h5"
                                            component="h1"
                                            sx={{ lineHeight: 1.1 }}
                                        >
                                            Edit survey
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                mt: 0.5,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {title || "Untitled survey"}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        size="small"
                                        label={`${survey.questions.length} ${
                                            survey.questions.length === 1
                                                ? "question"
                                                : "questions"
                                        }`}
                                        sx={{
                                            display: {
                                                xs: "none",
                                                md: "flex",
                                            },
                                            backgroundColor:
                                                "rgba(0, 149, 61, 0.09)",
                                            color: "primary.main",
                                        }}
                                    />
                                    {canShareSurveys && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<ShareOutlinedIcon />}
                                            onClick={() =>
                                                setShareDialogOpen(true)
                                            }
                                        >
                                            Share
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "minmax(0, 1fr)",
                                        md: "minmax(0, 1fr) 320px",
                                    },
                                    alignItems: "stretch",
                                    gap: 3,
                                }}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{
                                        ...surfaceSx,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            px: { xs: 2, sm: 3 },
                                            py: 2.25,
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            component="h2"
                                        >
                                            Survey details
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.35 }}
                                        >
                                            Introduce the survey and give
                                            respondents clear context.
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <CardContent
                                        sx={{
                                            p: { xs: 2, sm: 3 },
                                            display: "flex",
                                            flex: 1,
                                            flexDirection: "column",
                                            gap: 2,
                                            "&:last-child": {
                                                pb: { xs: 2, sm: 3 },
                                            },
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            value={title}
                                            onChange={handleTitleChange}
                                            id="surveyTitle"
                                            label="Survey title"
                                        />
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            value={introduction}
                                            onChange={handleIntroductionChange}
                                            id="surveyIntro"
                                            label="Introduction"
                                        />
                                    </CardContent>
                                </Card>

                                <Card
                                    variant="outlined"
                                    sx={{
                                        ...surfaceSx,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Box sx={{ px: 2.5, py: 2.25 }}>
                                        <Typography
                                            variant="h6"
                                            component="h2"
                                        >
                                            Response settings
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.35 }}
                                        >
                                            Control how responses are recorded.
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <CardContent
                                        sx={{
                                            p: 2.5,
                                            display: "flex",
                                            flex: 1,
                                            flexDirection: "column",
                                            "&:last-child": { pb: 2.5 },
                                        }}
                                    >
                                        <FormControlLabel
                                            sx={{
                                                m: 0,
                                                alignItems: "flex-start",
                                            }}
                                            control={
                                                <Switch
                                                    checked={anonymousAnswers}
                                                    onChange={
                                                        handleAnonymousAnswersChange
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box sx={{ pt: 0.25 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{ fontWeight: 800 }}
                                                    >
                                                        Anonymous answers
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.35 }}
                                                    >
                                                        Hide respondent names
                                                        in survey results.
                                                    </Typography>
                                                </Box>
                                            }
                                        />

                                        <Divider sx={{ my: 2.25 }} />

                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 1.25,
                                            }}
                                        >
                                            <AutoAwesomeOutlinedIcon
                                                sx={{
                                                    mt: 0.1,
                                                    color: "primary.main",
                                                }}
                                            />
                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 800 }}
                                                >
                                                    Changes save automatically
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.35 }}
                                                >
                                                    Edits are written as you
                                                    make them.
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ flex: 1, minHeight: 20 }} />

                                        <Button
                                            fullWidth
                                            color="error"
                                            variant="outlined"
                                            startIcon={<DeleteOutlineIcon />}
                                            onClick={() =>
                                                setDeleteConfirmDialogIsOpen(
                                                    true,
                                                )
                                            }
                                        >
                                            Delete survey
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Box>

                            <Box>
                                <Box
                                    sx={{
                                        mb: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography
                                            variant="h5"
                                            component="h2"
                                        >
                                            Questions
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.35 }}
                                        >
                                            Build the form in the order
                                            respondents will see it.
                                        </Typography>
                                    </Box>
                                </Box>

                                {survey.questions.length === 0 ? (
                                    <Card variant="outlined" sx={surfaceSx}>
                                        <Box
                                            sx={{
                                                minHeight: 260,
                                                px: 3,
                                                py: 5,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                textAlign: "center",
                                                color: "text.secondary",
                                            }}
                                        >
                                            <AssignmentOutlinedIcon
                                                sx={{
                                                    mb: 1.25,
                                                    fontSize: 44,
                                                    opacity: 0.38,
                                                }}
                                            />
                                            <Typography
                                                variant="h6"
                                                color="text.primary"
                                            >
                                                No questions yet
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5 }}
                                            >
                                                Add a question or link a
                                                resource from Files.
                                            </Typography>
                                            <Button
                                                sx={{ mt: 2 }}
                                                variant="contained"
                                                disableElevation
                                                startIcon={<AddIcon />}
                                                onClick={
                                                    handleAddComponentClick
                                                }
                                            >
                                                Add content
                                            </Button>
                                        </Box>
                                    </Card>
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2,
                                        }}
                                    >
                                        {survey.questions.map(
                                            (question, index) => (
                                                <SurveyQuestionEdit
                                                    key={question.key}
                                                    surveyKey={survey.key}
                                                    question={question}
                                                    index={index}
                                                    displayNumber={
                                                        questionDisplayNumbers[
                                                            index
                                                        ]
                                                    }
                                                    questionCount={
                                                        survey.questions.length
                                                    }
                                                    handleDeleteQuestionClick={
                                                        handleDeleteQuestionClick
                                                    }
                                                    handleQuestionMoveUpPressed={(
                                                        item,
                                                    ) =>
                                                        moveQuestion(item, -1)
                                                    }
                                                    handleQuestionMoveDownPressed={(
                                                        item,
                                                    ) =>
                                                        moveQuestion(item, 1)
                                                    }
                                                />
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Container>
                </Box>
            )}

            {userCanEdit === true && survey && (
                <FloatingAddContentButton
                    id="add-survey-content-button"
                    onClick={handleAddComponentClick}
                />
            )}

            <Snackbar
                open={statusIsVisible}
                autoHideDuration={6000}
                onClose={(event, reason) => {
                    if (reason !== "clickaway") {
                        setStatusIsVisible(false);
                    }
                }}
            >
                <Alert
                    onClose={() => setStatusIsVisible(false)}
                    severity={statusType}
                >
                    {statusMessage}
                </Alert>
            </Snackbar>

            {canShareSurveys && surveyInfoObject && (
                <ShareSurveyDialog
                    open={shareDialogOpen}
                    onClose={handleCloseShareDialog}
                    surveyInfo={surveyInfoObject}
                />
            )}

            <ConfirmDeleteDialog
                open={deleteConfirmDialogIsOpen}
                onClose={handleCloseConfirmDeleteDialog}
                inProgress={deleteInProgress}
                title="Delete survey?"
                detail={title || "Untitled survey"}
                message="Delete this survey and all of its responses?"
            />

            {selectExistingFileDialogOpen && (
                <ProjectSelectFileDialog
                    open={selectExistingFileDialogOpen}
                    onClose={handleSelectExistingFileClose}
                    title="Add resource from Files"
                    description="Browse your folders and choose a resource to include in this survey."
                    selectionHint="Select a file to add it to the survey."
                />
            )}

            <Menu
                id="survey-content-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{
                    "aria-labelledby": "add-survey-content-button",
                }}
                // The trigger sits at the bottom right of the viewport, so the
                // menu has to open upwards from its right edge to stay on screen.
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                slotProps={{
                    paper: {
                        sx: {
                            mb: 1,
                            minWidth: 230,
                            borderRadius: 2,
                        },
                    },
                }}
            >
                <MenuItem onClick={handleAddQuestionClick}>
                    <AssignmentOutlinedIcon
                        fontSize="small"
                        sx={{ mr: 1.25, color: "primary.main" }}
                    />
                    Add question
                </MenuItem>
                <MenuItem onClick={handleNewExistingFilePressed}>
                    <InsertDriveFileOutlinedIcon
                        fontSize="small"
                        sx={{ mr: 1.25, color: "primary.main" }}
                    />
                    Add resource from Files
                </MenuItem>
            </Menu>

            <ToolbarWithLogo />
        </>
    );
}
